import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Check } from 'lucide-react';
import { AchievementDefinition } from './achievementDefinitions';

interface AchievementCardProps {
  achievement: AchievementDefinition;
  isUnlocked: boolean;
  unlockedAt?: string;
  progress?: { current: number; target: number };
  isRecent?: boolean;
  onClick?: () => void;
}

export const AchievementCard: React.FC<AchievementCardProps> = ({
  achievement,
  isUnlocked,
  unlockedAt,
  progress,
  isRecent = false,
  onClick
}) => {
  const progressPercent = progress ? Math.min((progress.current / progress.target) * 100, 100) : 0;

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        relative w-full aspect-square rounded-2xl p-3 flex flex-col items-center justify-center gap-1
        transition-all duration-300 cursor-pointer
        ${isUnlocked 
          ? isRecent
            ? 'bg-gradient-to-br from-amber-500/30 to-orange-600/30 border-2 border-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.4)]'
            : 'bg-gradient-to-br from-amber-500/20 to-orange-600/20 border border-amber-500/40'
          : 'bg-white/5 border border-white/10 hover:bg-white/10'
        }
      `}
    >
      {/* Badge emoji */}
      <span className={`text-3xl sm:text-4xl ${!isUnlocked && 'grayscale opacity-50'}`}>
        {achievement.badge}
      </span>
      
      {/* Name */}
      <span className={`text-[10px] sm:text-xs font-medium text-center leading-tight line-clamp-2 ${
        isUnlocked ? 'text-white' : 'text-white/50'
      }`}>
        {achievement.name}
      </span>
      
      {/* Points */}
      <span className={`text-[9px] sm:text-[10px] ${
        isUnlocked ? 'text-amber-400' : 'text-white/30'
      }`}>
        +{achievement.points} pts
      </span>

      {/* Status indicator */}
      <div className="absolute top-1.5 right-1.5">
        {isUnlocked ? (
          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
            <Check className="w-3 h-3 text-white" />
          </div>
        ) : (
          <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center">
            <Lock className="w-3 h-3 text-white/40" />
          </div>
        )}
      </div>

      {/* Progress bar for locked achievements with progress */}
      {!isUnlocked && progress && progress.target > 0 && (
        <div className="absolute bottom-1.5 left-2 right-2">
          <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
            />
          </div>
          <span className="text-[8px] text-white/40 mt-0.5 block text-center">
            {progress.current}/{progress.target}
          </span>
        </div>
      )}

      {/* Pulse animation for recent unlocks */}
      {isRecent && (
        <motion.div
          className="absolute inset-0 rounded-2xl border-2 border-amber-400"
          animate={{ 
            opacity: [0.5, 1, 0.5],
            scale: [1, 1.02, 1]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
      )}
    </motion.button>
  );
};

export default AchievementCard;
